'use client';

import Select from '../components/Select';
import { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '../components/AuthProvider';
import { Footer } from '../components/Footer';

const CATEGORIES = ['Billing', 'Bug Report', 'Feature Request', 'Other'] as const;

export default function ContactPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<string>('Billing');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Pre-fill email once user loads
  const displayEmail = email || user?.email || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: displayEmail,
          category,
          subject,
          message,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong.');
        return;
      }

      setStatus('sent');
    } catch {
      setStatus('error');
      setErrorMsg('Failed to send. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 max-w-xl mx-auto w-full px-5 sm:px-8 py-16 sm:py-24">
        <Link
          href="/"
          className="-ml-1 mb-6 inline-flex min-h-11 items-center gap-1 px-1 text-sm text-muted hover:text-foreground"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold font-serif mb-3">Contact Us</h1>
        <p className="text-sm text-muted mb-10">
          Have a question about billing, found a bug, or want to request a feature? We&apos;d love to hear from you.
        </p>

        {status === 'sent' ? (
          <div className="card p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold font-serif mb-2">Message sent!</h2>
            <p className="text-sm text-muted mb-6">
              Thanks for reaching out. We&apos;ll get back to you at <strong>{displayEmail}</strong> as soon as we can.
            </p>
            <Link
              href="/"
              className="ui-button ui-button--primary"
            >
              Back to home
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={displayEmail}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="min-h-11 w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium mb-1.5">
                Category
              </label>
              <Select aria-label="Category"
                id="category"
                value={category}
                onValueChange={(value) => setCategory(value)}
                className="block w-full"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium mb-1.5">
                Subject
              </label>
              <input
                id="subject"
                type="text"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of your issue"
                className="min-h-11 w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-1.5">
                Message
              </label>
              <textarea
                id="message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us more..."
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
              />
            </div>

            {status === 'error' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="ui-button ui-button--primary w-full"
            >
              {status === 'sending' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        )}
      </div>

      <Footer />
    </div>
  );
}
